import { useState, useRef, useEffect, useCallback } from "react";
import type { ChangeEvent } from "react";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import "./FilterDropdown.scss";

interface CategoryGroup {
  category: string;
  items: { value: string; label: string }[];
}

interface FilterDropdownProps {
  label: string;
  title: string;
  mode: "checkbox" | "radio";
  selectedValues: string[];
  // eslint-disable-next-line no-unused-vars
  onApply: (values: string[]) => void;
  /** Flat options for languages / country */
  options?: { value: string; label: string }[];
  /** Top-pinned options shown above divider (e.g. English, Spanish) */
  topOptions?: { value: string; label: string }[];
  /** Categorised options for skills / tools (2-col grid with +N more) */
  categoryGroups?: CategoryGroup[];
  /** Show search input */
  searchable?: boolean;
  /** How many items per category before collapsing behind "+N more" */
  categoryPreviewCount?: number;
}

function FilterDropdown({
  label,
  title,
  mode,
  selectedValues,
  onApply,
  options = [],
  topOptions = [],
  categoryGroups = [],
  searchable = true,
  categoryPreviewCount = 4,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Sync local state when parent selectedValues change (e.g. reset)
  useEffect(() => {
    setLocalSelected(selectedValues);
  }, [selectedValues]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setExpandedCategories(new Set());
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      setLocalSelected(selectedValues);
      setSearchTerm("");
      setExpandedCategories(new Set());
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (value: string) => {
    if (mode === "radio") {
      setLocalSelected([value]);
    } else {
      setLocalSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    }
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    onApply(localSelected);
    setIsOpen(false);
    setSearchTerm("");
    setExpandedCategories(new Set());
  };

  const toggleCategoryExpand = useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const filterBySearch = (items: { value: string; label: string }[]) => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(term));
  };

  const count = selectedValues.length;
  const isCategorised = categoryGroups.length > 0;

  return (
    <div className="filter-bar__section-wrapper">
      <button type="button" ref={triggerRef} className="filter-bar__section" onClick={handleToggle}>
        <span className="filter-bar__label">{label}</span>
        <span className="filter-bar__right">
          {count > 0 && <span className="filter-bar__badge">{count}</span>}
          <ExpandMoreIcon
            sx={{ fontSize: 24 }}
            className={`filter-bar__chevron ${isOpen ? "filter-bar__chevron--open" : ""}`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="filter-dropdown__panel" ref={panelRef}>
          <div className="filter-dropdown__panel-header">
            <h3 className="filter-dropdown__panel-title">{title}</h3>
          </div>

          {searchable && (
            <div className="filter-dropdown__search">
              <SearchIcon sx={{ fontSize: 20 }} className="filter-dropdown__search-icon" />
              <input
                type="text"
                className="filter-dropdown__search-input"
                placeholder="Search"
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          <div className="filter-dropdown__content">
            {/* Top pinned options (e.g. English, Spanish for languages) */}
            {topOptions.length > 0 && (
              <>
                {filterBySearch(topOptions).map((opt) => (
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label key={`top-${opt.value}`} className="filter-dropdown__option">
                    <input
                      type={mode === "radio" ? "radio" : "checkbox"}
                      checked={localSelected.includes(opt.value)}
                      onChange={() => handleSelect(opt.value)}
                      className={`filter-dropdown__input filter-dropdown__input--${mode}`}
                    />
                    <span className="filter-dropdown__option-label">{opt.label}</span>
                  </label>
                ))}
                <div className="filter-dropdown__divider" />
              </>
            )}

            {/* Categorised layout for skills / tools */}
            {isCategorised &&
              categoryGroups.map((group) => {
                const filtered = filterBySearch(group.items);
                if (filtered.length === 0) return null;

                const isExpanded = expandedCategories.has(group.category);
                const visibleItems = isExpanded ? filtered : filtered.slice(0, categoryPreviewCount);
                const hiddenCount = filtered.length - categoryPreviewCount;

                return (
                  <div key={group.category} className="filter-dropdown__category">
                    <div className="filter-dropdown__category-title">{group.category}</div>
                    <div className="filter-dropdown__category-grid">
                      {visibleItems.map((opt) => (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label key={opt.value} className="filter-dropdown__option">
                          <input
                            type="checkbox"
                            checked={localSelected.includes(opt.value)}
                            onChange={() => handleSelect(opt.value)}
                            className="filter-dropdown__input filter-dropdown__input--checkbox"
                          />
                          <span className="filter-dropdown__option-label">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {!isExpanded && hiddenCount > 0 && (
                      <button
                        type="button"
                        className="filter-dropdown__show-more"
                        onClick={() => toggleCategoryExpand(group.category)}
                      >
                        + {hiddenCount} more
                      </button>
                    )}
                    {isExpanded && hiddenCount > 0 && (
                      <button
                        type="button"
                        className="filter-dropdown__show-more"
                        onClick={() => toggleCategoryExpand(group.category)}
                      >
                        Show less
                      </button>
                    )}
                    <div className="filter-dropdown__divider" />
                  </div>
                );
              })}

            {/* Flat options (languages remainder / country) */}
            {!isCategorised &&
              filterBySearch(options).map((opt) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label key={opt.value} className="filter-dropdown__option">
                  <input
                    type={mode === "radio" ? "radio" : "checkbox"}
                    checked={localSelected.includes(opt.value)}
                    onChange={() => handleSelect(opt.value)}
                    className={`filter-dropdown__input filter-dropdown__input--${mode}`}
                  />
                  <span className="filter-dropdown__option-label">{opt.label}</span>
                </label>
              ))}
          </div>

          <div className="filter-dropdown__footer">
            <button type="button" className="filter-dropdown__clear-btn" onClick={handleClearAll}>
              Clear all
            </button>
            <button type="button" className="filter-dropdown__apply-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
